import React, { useEffect, useState } from "react";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { GridColDef, GridInitialState } from "@mui/x-data-grid-premium";
import { BrandSummary_T } from "../../../types/Brand";
import { getDateValue, renderDateCell, renderNumberCell } from "utils/dataGrid";
import GaneshDataGrid from "../../DataGrid/GaneshDataGrid";

interface BrandFillRatesByCountryTableProps {
  brand_id: string;
}

const defaultGridState: GridInitialState = {
  filter: {
    filterModel: {
      items: [],
    },
  },
  sorting: { sortModel: [{ field: "country_name", sort: "asc" }] },
  pagination: { paginationModel: { pageSize: 25, page: 0 } },
  pinnedColumns: { left: ["country_name", "country_code"] },
  columns: { columnVisibilityModel: {} },
  density: "compact",
  aggregation: {
    model: {
      total_poi_count_filtered: "sum",
    },
  },
};

const columns_def: GridColDef[] = [
  {
    field: "country_name",
    headerName: "Country",
    description:
      "A country where this brand has physical locations, derived from the distinct countries of places with a chain_id for this brand.",
    type: "string",
    width: 200,
  },
  {
    field: "country_code",
    headerName: "Country Code",
    description: "ISO 3166-1 alpha-2 for this country.",
    type: "string",
    width: 100,
  },
  {
    field: "total_poi_count_filtered",
    headerName: "Active Count",
    description:
      "The number of non-ignored, non-duplicate places associated with this brand's chain_id.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_location_id",
    headerName: "POI Location ID Fill Rate",
    description:
      "The proportion of active places with a non-null location_id value, calculated as the count of active places with a location_id divided by the active count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_poi_name",
    headerName: "POI Name Fill Rate ",
    description:
      "The proportion of active places with a non-null name value, calculated as the count of active places with a name divided by the active count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_poi_address",
    headerName: "POI Address Fill Rate",
    description:
      "The proportion of active places with a non-null address value, calculated as the count of active places with an address divided by the active count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_coordinates",
    headerName: "POI Coordinates Rate",
    description:
      "The proportion of active places with non-null coordinate values, calculated as the count of active places with coordinates divided by the active count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_business_category_id",
    headerName: "POI Business Category Fill Rate",
    description:
      "The proportion of active places with a non-null business category value, calculated as the count of active places with a business category divided by the active count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "fill_rate_building_polygon",
    headerName: "POI Polygon Fill Rate",
    description:
      "The proportion of active places with a non-null polygon value, calculated as the count of active places with a polygon divided by the active count.",
    type: "number",
    width: 150,
    renderCell: renderNumberCell,
  },
  {
    field: "query_last_executed",
    headerName: "Data Last Updated",
    description:
      "The date when this data was retrieved and aggregated from the places table in the production database.",
    type: "date",
    width: 200,
    valueGetter: getDateValue,
    renderCell: renderDateCell,
  },
];

const BrandFillRatesByCountryTable: React.FC<
  BrandFillRatesByCountryTableProps
> = ({ brand_id }) => {
  const [brandFillRatesByCountryData, setBrandFillRatesByCountryData] =
    useState<BrandSummary_T[]>([]);
  const [loading, setLoading] = useState(true);

  const { showError } = useError();
  const { apiRequest } = useApi();

  useEffect(() => {
    if (brand_id) {
      const fetchBrandFillRatesData = async () => {
        try {
          const response = await apiRequest<BrandSummary_T[]>(
            `/brand_reports/brand_fill_rates/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            setBrandFillRatesByCountryData(response.data);
          }
        } catch (error) {
          console.error(error);
          showError(
            `Failed to fetch brand fill rate by country data for brand ID ${brand_id}.`
          );
        } finally {
          setLoading(false);
        }
      };
      fetchBrandFillRatesData();
    }
  }, [brand_id]);

  return (
    <GaneshDataGrid
      loading={loading}
      data={brandFillRatesByCountryData}
      columns={columns_def}
      gridId="brandFillRatesGrid"
      defaultGridState={defaultGridState}
    />
  );
};

export default BrandFillRatesByCountryTable;
